import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Lock, Megaphone, ArrowLeft } from "lucide-react";

const EMPTY = {
  ga4_id: "",
  gtm_id: "",
  google_site_verification: "",
  google_ads_id: "",
  google_ads_label: "",
  youtube_ads_label: "",
  clarity_id: "",
  fb_pixel_id: "",
};

const TABS = [
  { id: "google", label: "Google" },
  { id: "youtube", label: "YouTube" },
  { id: "facebook", label: "Facebook" },
];

function Status({ row, testId }) {
  if (!row || row.state === "empty") {
    return <span className="text-xs font-medium text-slate-400" data-testid={testId}>Not set</span>;
  }
  if (row.state === "live") {
    return <span className="text-xs font-semibold text-emerald-700" data-testid={testId}>Live & connected</span>;
  }
  return (
    <span className="text-xs font-semibold text-red-600" data-testid={testId}>
      Failed{row.detail ? ` — ${row.detail}` : ""}
    </span>
  );
}

function Field({ label, testId, value, onChange, placeholder, hint, status, statusId }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        <Status row={status} testId={statusId} />
      </div>
      <Input data-testid={testId} className="font-mono-nums" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
    </div>
  );
}

export default function AdminAds() {
  const [active, setActive] = useState(false);
  const [ads, setAds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [connections, setConnections] = useState(null);
  const [tab, setTab] = useState("google");

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/merchant/config/ads/test");
      setConnections(data.connections || {});
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setChecking(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/config/ads");
      setActive(Boolean(data.active));
      setAds({ ...EMPTY, ...(data.ads || {}) });
      if (data.active) check();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/ads", ads);
      setAds({ ...EMPTY, ...(data.ads || ads) });
      toast.success("Ads Tool saved. Checking each tag now.");
      await check();
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (value) => setAds({ ...ads, [key]: value });
  const row = (key) => connections?.[key];

  if (!ads) {
    return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl pb-16">
      <Link to="/admin/addons" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> App Store
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-violet-600" /> Ads Tool
          </h1>
          <p className="text-slate-500 text-sm mt-1">Tags load on your website storefront: page views, product views, add to cart, checkout, and purchases. They do not run inside the Android app.</p>
        </div>
        {active && (
          <Button onClick={save} disabled={saving} data-testid="ads-save-top" className="bg-slate-900 hover:bg-slate-800">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
          </Button>
        )}
      </div>

      {!active && (
        <Card className="p-5 border-violet-200 bg-violet-50" data-testid="ads-locked">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-violet-700 mt-0.5" />
            <div>
              <div className="font-semibold text-violet-950">Ads Tool is locked</div>
              <p className="text-sm text-violet-800 mt-1">Unlock it from App Store (₹2,000/year) to connect Google, YouTube, and Facebook ads.</p>
              <Link to="/admin/addons"><Button className="mt-3 rounded-full bg-violet-600 hover:bg-violet-700" data-testid="ads-goto-store">Open App Store</Button></Link>
            </div>
          </div>
        </Card>
      )}

      <div className={`flex gap-2 ${active ? "" : "opacity-60 pointer-events-none"}`} data-testid="ads-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`ads-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            {t.label}
          </button>
        ))}
        {active && (
          <button type="button" onClick={check} disabled={checking} data-testid="ads-check" className="ml-auto text-sm text-violet-700 font-semibold">
            {checking ? "Checking…" : "Check connection"}
          </button>
        )}
      </div>

      {tab === "google" && (
        <Card className={`p-5 space-y-4 ${active ? "" : "opacity-60 pointer-events-none"}`} data-testid="ads-panel-google">
          <div>
            <div className="font-display font-bold text-slate-900">Google Ads, Analytics, and Tag Manager</div>
            <ol className="mt-2 text-sm text-slate-600 list-decimal pl-5 space-y-1" data-testid="ads-help-google">
              <li>GA4: Admin → Data streams → your web stream → copy the Measurement ID (G-…).</li>
              <li>Google Ads: Tools → Google tag → copy the Tag ID (AW-…). For purchases, open Goals → Conversions → your purchase action → Tag setup, and paste only the label after the slash in AW-123/AbCd.</li>
              <li>Tag Manager is optional. If this container already fires GA4 or the Google tag, leave those ID fields empty so visits are not counted twice.</li>
              <li>Search Console: Add property → HTML tag → paste the content value, then click Verify in Search Console after saving here.</li>
              <li>Save, then open your store in a browser (not the Android app) and confirm the visit in GA4 Realtime or Google Tag Assistant.</li>
            </ol>
          </div>
          <Field label="Google Analytics 4 Measurement ID" testId="ads-ga4" statusId="ads-status-ga4" value={ads.ga4_id} onChange={set("ga4_id")} placeholder="G-XXXXXXXXXX" status={row("ga4_id")} hint="Counts visits, product views, add to cart, checkout, and purchases." />
          <Field label="Google Ads tag ID" testId="ads-aw" statusId="ads-status-aw" value={ads.google_ads_id} onChange={set("google_ads_id")} placeholder="AW-123456789" status={row("google_ads_id")} hint="Same tag covers Search, Display, and YouTube remarketing." />
          <Field label="Purchase conversion label" testId="ads-aw-label" statusId="ads-status-aw-label" value={ads.google_ads_label} onChange={set("google_ads_label")} placeholder="AbCDefGhIjK" status={row("google_ads_label")} hint="Fired when an order is placed (paid, COD, or WhatsApp)." />
          <Field label="Google Tag Manager container ID" testId="ads-gtm" statusId="ads-status-gtm" value={ads.gtm_id} onChange={set("gtm_id")} placeholder="GTM-XXXXXX" status={row("gtm_id")} />
          <Field label="Google Search Console verification" testId="ads-gsc" statusId="ads-status-gsc" value={ads.google_site_verification} onChange={set("google_site_verification")} placeholder="content value of google-site-verification" status={row("google_site_verification")} />
          <Field label="Microsoft Clarity project ID" testId="ads-clarity" statusId="ads-status-clarity" value={ads.clarity_id} onChange={set("clarity_id")} placeholder="abcdefghij" status={row("clarity_id")} hint="clarity.microsoft.com → Settings → Setup. Session recordings, not ad billing." />
        </Card>
      )}

      {tab === "youtube" && (
        <Card className={`p-5 space-y-4 ${active ? "" : "opacity-60 pointer-events-none"}`} data-testid="ads-panel-youtube">
          <div>
            <div className="font-display font-bold text-slate-900">YouTube ads</div>
            <ol className="mt-2 text-sm text-slate-600 list-decimal pl-5 space-y-1" data-testid="ads-help-youtube">
              <li>YouTube campaigns run from Google Ads. The AW- tag on the Google tab is the tag YouTube uses for remarketing. There is no separate YouTube pixel.</li>
              <li>In Google Ads, link the YouTube channel, then create a Video campaign. Audience signals use people who visited this store.</li>
              <li>If the video campaign has its own conversion action, paste that action’s label here. Purchases then report to both the Google label and this YouTube label.</li>
              <li>Leave this label empty when Search and YouTube share one purchase conversion.</li>
            </ol>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="ads-youtube-connection">
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium text-slate-800">Google tag used by YouTube</span>
              <Status row={row("google_ads_id")} testId="ads-status-youtube-tag" />
            </div>
            <p className="text-xs text-slate-500 mt-1 font-mono-nums">{ads.google_ads_id || "Add the AW- ID on the Google tab."}</p>
          </div>
          <Field label="YouTube conversion label" testId="ads-yt-label" statusId="ads-status-youtube-label" value={ads.youtube_ads_label} onChange={set("youtube_ads_label")} placeholder="Optional, only if YouTube has its own conversion" status={row("youtube_ads_label")} />
        </Card>
      )}

      {tab === "facebook" && (
        <Card className={`p-5 space-y-4 ${active ? "" : "opacity-60 pointer-events-none"}`} data-testid="ads-panel-facebook">
          <div>
            <div className="font-display font-bold text-slate-900">Facebook and Instagram</div>
            <ol className="mt-2 text-sm text-slate-600 list-decimal pl-5 space-y-1" data-testid="ads-help-facebook">
              <li>Meta Events Manager → Data sources → your pixel → copy the numeric Pixel ID. Paste the number only, not the whole code snippet.</li>
              <li>Save, open the store website, and install the Meta Pixel Helper extension. It should show PageView. Open a product (ViewContent), add it to cart (AddToCart), go to checkout (InitiateCheckout), and place an order (Purchase).</li>
              <li>In Ads Manager, create the campaign and choose this pixel. Website conversions need the Purchase event, which fires on the order confirmation page.</li>
              <li>Test events in Events Manager can take a few minutes. A green “Live & connected” here means the pixel ID is valid and Meta’s script responded. It does not mean a campaign is spending.</li>
            </ol>
          </div>
          <Field label="Meta Pixel ID" testId="ads-pixel" statusId="ads-status-pixel" value={ads.fb_pixel_id} onChange={set("fb_pixel_id")} placeholder="123456789012345" status={row("fb_pixel_id")} hint="Facebook and Instagram use this same pixel." />
        </Card>
      )}

      {active && (
        <Button onClick={save} disabled={saving} data-testid="ads-save-bottom" className="bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save connectors
        </Button>
      )}
    </div>
  );
}
